<template>
  <header :class="['site-header', { scrolled: isScrolled }]">
    <div class="header-inner">
      <RouterLink class="brand" to="/" aria-label="Blue Speed Construction home">
        <img
          class="brand-logo"
          src="@/assets/bluespeedlogo-transparent.png"
          alt="Blue Speed Construction Pte Ltd"
        />
      </RouterLink>

      <nav class="desktop-nav" aria-label="Main navigation">
        <RouterLink v-for="link in navLinks" :key="link.to" :to="link.to">
          {{ link.label }}
        </RouterLink>
      </nav>

      <div class="header-actions">
        <a class="whatsapp-link" :href="whatsappUrl" target="_blank" rel="noopener noreferrer">
          WhatsApp Us
        </a>
        <RouterLink class="btn btn-primary btn-sm" to="/contact">
          Enquire About a Project
          <ArrowRight />
        </RouterLink>

        <button
          class="mobile-menu-btn"
          type="button"
          :aria-expanded="mobileMenuOpen"
          aria-label="Toggle navigation"
          @click="mobileMenuOpen = !mobileMenuOpen"
        >
          <X v-if="mobileMenuOpen" />
          <Menu v-else />
        </button>
      </div>
    </div>

    <transition name="mobile-menu">
      <div v-if="mobileMenuOpen" class="mobile-menu-panel">
        <RouterLink
          v-for="link in navLinks"
          :key="`m-${link.to}`"
          :to="link.to"
          @click="closeMobileMenu"
        >
          {{ link.label }}
        </RouterLink>

        <a
          class="mobile-menu-cta"
          :href="whatsappUrl"
          target="_blank"
          rel="noopener noreferrer"
          @click="closeMobileMenu"
        >
          WhatsApp Blue Speed
          <ArrowRight />
        </a>
      </div>
    </transition>
  </header>
</template>

<script>
import { ArrowRight, Menu, X } from "lucide-vue-next";

import { whatsappLink } from "@/config/company";
import { NAV_LINKS } from "@/config/navigation";

export default {
  name: "StandardNavBar",

  components: {
    ArrowRight,
    Menu,
    X,
  },

  data() {
    return {
      isScrolled: false,
      mobileMenuOpen: false,
      navLinks: NAV_LINKS,
      whatsappUrl: whatsappLink(),
    };
  },

  watch: {
    $route() {
      this.mobileMenuOpen = false;
    },
  },

  mounted() {
    this.handleScroll();
    window.addEventListener("scroll", this.handleScroll, { passive: true });
  },

  beforeUnmount() {
    window.removeEventListener("scroll", this.handleScroll);
  },

  methods: {
    handleScroll() {
      this.isScrolled = window.scrollY > 24;
    },

    closeMobileMenu() {
      this.mobileMenuOpen = false;
    },
  },
};
</script>

<style scoped>
.site-header {
  position: fixed;
  z-index: 100;
  top: 0;
  left: 0;
  width: 100%;
  border-bottom: 1px solid transparent;
  transition: 220ms ease;
}

.site-header.scrolled {
  border-color: rgba(11, 37, 69, 0.1);
  background: rgba(247, 251, 255, 0.95);
  backdrop-filter: blur(18px);
  box-shadow: 0 8px 28px rgba(20, 60, 105, 0.05);
}

.header-inner {
  width: min(1360px, calc(100% - 64px));
  min-height: 84px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 32px;
}

.brand {
  display: inline-flex;
  align-items: center;
  width: fit-content;
  text-decoration: none;
}

.brand-logo {
  display: block;
  width: 205px;
  max-height: 45px;
  object-fit: contain;
  object-position: left center;
}

.desktop-nav {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 30px;
}

.desktop-nav a,
.whatsapp-link {
  position: relative;
  color: #4e6d8c;
  font-size: 13px;
  font-weight: 500;
  text-decoration: none;
  transition: color 180ms ease;
}

.desktop-nav a::after {
  content: "";
  position: absolute;
  left: 0;
  bottom: -6px;
  width: 0;
  height: 2px;
  background: #0b5dbb;
  transition: width 200ms ease;
}

.desktop-nav a:hover::after,
.desktop-nav a.router-link-active::after {
  width: 100%;
}

.desktop-nav a:hover,
.whatsapp-link:hover {
  color: #0b2545;
}

.desktop-nav a.router-link-active {
  color: #0b2545;
  font-weight: 600;
}

.header-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 20px;
}

.mobile-menu-btn {
  display: none;
  width: 42px;
  height: 42px;
  border: 1px solid #d2e0ec;
  background: #ffffff;
  color: #0b2545;
}

.mobile-menu-btn svg {
  width: 20px;
  height: 20px;
}

.mobile-menu-panel {
  display: none;
}

.mobile-menu-enter-active,
.mobile-menu-leave-active {
  transition: 180ms ease;
}

.mobile-menu-enter-from,
.mobile-menu-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

@media (max-width: 1120px) {
  .desktop-nav,
  .whatsapp-link,
  .header-actions .btn {
    display: none;
  }

  .header-inner {
    grid-template-columns: 1fr auto;
  }

  .mobile-menu-btn {
    display: grid;
    place-items: center;
  }

  .mobile-menu-panel {
    display: grid;
    gap: 4px;
    padding: 16px 32px 24px;
    border-top: 1px solid #d8e4ee;
    background: rgba(247, 251, 255, 0.98);
    backdrop-filter: blur(18px);
  }

  .mobile-menu-panel a {
    padding: 16px 0;
    border-bottom: 1px solid #dfeaf3;
    color: #4e6d8c;
    font-size: 14px;
    text-decoration: none;
  }

  .mobile-menu-panel a.router-link-active {
    color: #0b5dbb;
    font-weight: 600;
  }

  .mobile-menu-panel .mobile-menu-cta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 10px;
    padding: 16px;
    border: 0;
    background: #ef3b2d;
    color: #ffffff;
    font-weight: 700;
  }

  .mobile-menu-panel .mobile-menu-cta svg {
    width: 17px;
    height: 17px;
  }
}

@media (max-width: 760px) {
  .header-inner {
    width: min(100% - 32px, 1360px);
    min-height: 72px;
  }

  .brand-logo {
    width: 168px;
    max-height: 40px;
  }
}
</style>
